import { type Accessor, createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { createExtendedPowerSyncStatus } from "@comandero/shared/solid";

/**
 * Resolución de una entidad de detalle en una app local-first.
 *
 * El problema: al abrir el detalle de una orden, una mesa o un producto del
 * menú (p.ej. restaurar el ticket activo persistido, o `/organizations/$orgId`),
 * la fila puede:
 *   - existir localmente → mostrarla;
 *   - **no haberse sincronizado aún** (sync inicial en curso) → seguir cargando;
 *   - **no existir** (borrada / id inválido / ticket viejo) → "no encontrado";
 *   - **no ser accesible** (las sync rules de PowerSync NO bajan filas sin
 *     permiso, así que desde el cliente "sin permiso" ≈ "ausente tras
 *     sincronizar") → "sin acceso".
 *
 * Sin este manejo, una entidad ausente deja la pantalla **cargando para
 * siempre** o, peor, mostrando una vista vacía engañosa (un ticket sin líneas
 * que parece una orden válida). Aquí distinguimos los estados para dar feedback
 * correcto.
 */
type EntityResolution = "loading" | "ready" | "not-found" | "offline";

export interface ResolutionInputs {
  /** La entidad está presente localmente. */
  hasEntity: boolean;
  /** La query aún no ha resuelto (sin resultado asentado todavía). */
  isPending: boolean;
  /** PowerSync completó la sincronización inicial. */
  hasSynced: boolean;
  /** Hay conexión de red. */
  isOnline: boolean;
  /** Venció la red de seguridad mientras esperábamos (evita cargar para siempre). */
  timedOut: boolean;
}

/**
 * Núcleo PURO y testeable de la máquina de estados (sin reactividad).
 *
 * - presente → `ready`
 * - query pendiente → `loading`
 * - asentada y ausente:
 *     - ya sincronizó (o venció el timeout) → `not-found` (no existe / sin acceso)
 *     - sin conexión y nunca sincronizó → `offline` (no se puede saber aún)
 *     - online pero aún sin sync inicial → `loading` (esperar a que baje)
 */
export function resolveEntityState(input: ResolutionInputs): EntityResolution {
  if (input.hasEntity) {
    return "ready";
  }
  if (input.isPending) {
    return "loading";
  }
  if (input.hasSynced || input.timedOut) {
    return "not-found";
  }
  if (!input.isOnline) {
    return "offline";
  }
  return "loading";
}

const DEFAULT_RESOLUTION_TIMEOUT_MS = 8000;

interface EntityResolutionOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  /** `true` cuando la entidad está cargada localmente. */
  hasEntity: Accessor<boolean>;
  /** `true` mientras la query no tiene resultado asentado. */
  isPending: Accessor<boolean>;
  /** Red de seguridad: ms esperando (online, sin sync) antes de declarar not-found. */
  timeoutMs?: number;
}

/**
 * Wrapper reactivo: combina la query de la entidad con el estado de sync de
 * PowerSync y una red de seguridad por timeout. Devuelve el estado de resolución.
 */
export function createEntityResolution(
  options: EntityResolutionOptions,
): Accessor<EntityResolution> {
  const status = createExtendedPowerSyncStatus(options.db);
  const [timedOut, setTimedOut] = createSignal(false);

  // Arranca el timeout solo durante la ventana de espera real (ausente, online,
  // sin sync inicial). Si la entidad llega o cambia el estado, se cancela/reinicia.
  createEffect(() => {
    const waiting =
      !options.hasEntity() && !options.isPending() && !status().hasSynced && status().isOnline;
    if (!waiting) {
      setTimedOut(false);
      return;
    }
    const id = setTimeout(() => {
      setTimedOut(true);
    }, options.timeoutMs ?? DEFAULT_RESOLUTION_TIMEOUT_MS);
    onCleanup(() => {
      clearTimeout(id);
    });
  });

  const resolution = createMemo(() =>
    resolveEntityState({
      hasEntity: options.hasEntity(),
      isPending: options.isPending(),
      hasSynced: status().hasSynced,
      isOnline: status().isOnline,
      timedOut: timedOut(),
    }),
  );
  return resolution;
}
